import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import PageHeader from "@/components/layout/PageHeader";
import SectionHeading from "@/components/motion/SectionHeading";
import Reveal from "@/components/motion/Reveal";
import CtaBand from "@/components/layout/CtaBand";

const solutionImages = [
  "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1504307651254-35680f356dfd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&auto=format&fit=crop",
];

const Solutions = () => {
  const { t } = useLanguage();

  return (
    <>
      <PageHeader chapter={t.solutionsPage.chapter} title={t.solutionsPage.title} subtitle={t.solutionsPage.subtitle} testid="solutions-header" />

      <section data-testid="solutions-list" className="bg-ink py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-16 lg:space-y-24">
          {t.solutionsSection.items.map((item, i) => (
            <div
              key={item.num}
              data-testid={`solution-block-${item.num}`}
              className="grid gap-8 lg:grid-cols-12 lg:items-center"
            >
              <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <Reveal>
                  <div className="group relative overflow-hidden border border-line">
                    <img
                      src={solutionImages[i]}
                      alt={item.title}
                      loading="lazy"
                      className="aspect-[16/10] w-full object-cover grayscale-[35%] transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                    />
                  </div>
                </Reveal>
              </div>
              <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <Reveal>
                  <p className="font-display text-7xl lg:text-8xl font-black text-outline">{item.num}</p>
                  <h2 className="mt-2 font-display font-bold uppercase tracking-tight leading-[0.95] text-3xl sm:text-4xl text-cream">
                    {item.title}
                  </h2>
                  <p className="mt-4 max-w-xl text-base leading-relaxed text-fog">{item.text}</p>
                </Reveal>
                <Reveal delay={0.12}>
                  <Link
                    to="/devis"
                    data-testid={`solution-quote-link-${item.num}`}
                    className="group mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-brand hover:text-brand-hover"
                  >
                    {t.solutionsPage.cta}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="solutions-process" className="border-t border-line bg-surface py-20 lg:py-32">
        <div className="absolute" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading chapter={t.process.chapter} title={t.process.title} subtitle={t.process.subtitle} testid="solutions-process-heading" />
          <div className="grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
            {t.process.steps.map((step, i) => (
              <Reveal key={step.num} delay={0.06 * i} className="h-full">
                <div className="group h-full bg-panel p-7 transition-colors hover:bg-[#20262e]">
                  <div className="flex items-baseline gap-4">
                    <span className="font-display text-4xl font-black text-brand">{step.num}</span>
                    <span className="h-px flex-1 bg-line transition-colors group-hover:bg-brand/50" aria-hidden="true" />
                  </div>
                  <h3 className="mt-4 font-display text-2xl font-bold uppercase tracking-wide text-cream">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-fog">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
};

export default Solutions;
